import React, { Fragment } from "react";
import { Html } from "@base/reactUtil/ReactHelper.comp";

export class ReactHelper {
  static joinWith(joinElement: React.ReactNode, ...args: any) {
    return this.join([...args], joinElement);
  }

  static join(args: any[], joinElement: React.ReactNode) {
    const ret: React.ReactNode[] = [];
    for (const a of args) {
      if (a) {
        if (ret.length > 0) {
          const br = <Fragment key={ret.length}>{joinElement}</Fragment>;
          ret.push(br);
        }
        ret.push(<Fragment key={ret.length}>{a}</Fragment>);
      }
    }
    return ret;
  }

  static createHtmlSpan(html: string, className?: string) {
    return <Html tag="span" html={html} className={className} />
  }

  static createHtmlDiv(html: string, className?: string) {
    return <Html tag="div" html={html} className={className} />
  }

  static assignRef<T>(ref: React.Ref<T>, value: T | null) {
    if (ref && Object.prototype.hasOwnProperty.call(ref, "current")) {
      const current = (ref as any).current;
      if (current === value)
        return;
    }

    if (typeof ref === "function") {
      ref(value);
    } else if (ref != null) {
      (ref as any).current = value;
    }
  }

  static assignRefs<T>(value: T | null, ...refs: (React.Ref<T> | undefined)[]) {
    refs.forEach((ref) => {
      if (ref)
        ReactHelper.assignRef(ref, value);
    });
  }

  static mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
    return (value: T | null) => ReactHelper.assignRefs(value, ...refs)
  }
}

export const useDebounce = <T,>(value: T, timeout: number, callback?: (_value: T) => void) => {
  const [state, setState] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setState(value);
      if (callback) callback(value);
    }, timeout);

    return () => clearTimeout(handler);
  }, [value, timeout, callback]);

  return state;
};

export const useDebouncedEffect = (effect: () => void, delay: number, deps: any[]) => {
  React.useEffect(() => {
    const handler = setTimeout(() => {
      effect();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export function useDelayedLoading(time: number) {
  const [loading, setLoading] = React.useState(false);
  const id = React.useRef<any>(0);

  function clear() {
    if (id.current !== undefined) {
      clearTimeout(id.current);
      id.current = undefined;
    }
  }

  React.useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  if (time === 0)
    return {
      loading,
      set: setLoading,
    };

  return {
    loading,
    set: (value: boolean) => {
      clear();
      if (value) {
        id.current = setTimeout(() => {
          setLoading(true);
        }, 300);
      } else {
        setLoading(false);
      }
    },
  };
}

export function useGeolocation(onPosition: PositionCallback, _options?: { watch?: boolean; onError?: PositionErrorCallback }) {
  const options: Required<typeof _options> = { watch: false, onError: (e) => console.log("getcurrentPosition failed: ", e), ..._options };
  React.useEffect(() => {
    let watchId: number | undefined;
    if (navigator.geolocation) {
      if (options.watch) {
        watchId = navigator.geolocation.watchPosition(onPosition, options.onError);
      } else {
        navigator.geolocation.getCurrentPosition(onPosition, options.onError);
      }
    }
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [onPosition, options.watch, options.onError]);
}